import {css} from 'aphrodite';
import React, {Dispatch, SetStateAction, useState} from 'react';

import {
  BLASTInputSource,
  BlastModalTrigger,
  trackBlastModalClose,
  trackBlastSubmission,
} from 'search/blast/BLASTTracking';
import {
  getSelectBlastTypeStepTitle,
  getSelectSequencesStepTitle,
  makeSequenceLabel,
  shouldUseShortProgram,
  stripWhitespace,
} from 'search/blast/BLASTUtil';
import {BLAST_MODAL_TITLE, styles} from 'search/blast/constants';
import {getBLASTParamsFromFilter, getDefaultBLASTParamsForTask} from 'search/blast/parameters/uiParamHelpers';
import SelectBlastTypePane from 'search/blast/SelectBlastTypePane';
import SelectSequencesPane from 'search/blast/SelectSequencesPane';
import SetParametersPane from 'search/blast/SetParametersPane';
import {
  BLASTFilter,
  BLASTNTask,
  BLASTOperator,
  BLASTPTask,
  BLASTXTask,
  Operator,
  TBLASTNTask,
} from 'search/SearchConstants';
import {ExpandableModal} from 'ui-library/modals/ExpandableModal';
import {ModalButton} from 'ui-library/modals/Modal';
import StepNav, {StepItemStatus, StepNavSize} from 'ui-library/StepNav';

import {validateBLASTSequence} from './validation';

export enum StepKey {
  SelectBlastType = 'SelectBlastType',
  SelectSequences = 'SelectSequences',
  SetParameters = 'SetParameters',
}

function getStepModalButtons(
  step: StepKey,
  setCurrentStepKey: Dispatch<SetStateAction<StepKey>>,
  sequenceWithoutWhitespace: string,
  filter: BLASTFilter,
  setFilter: Dispatch<SetStateAction<BLASTFilter>>,
  fileExtractionsInProgress: number,
  onRequestSubmit: (newFilter: BLASTFilter) => void,
  inputSources: Record<string, BLASTInputSource>,
  setInputSources: Dispatch<SetStateAction<Record<string, BLASTInputSource>>>
): Array<ModalButton> {
  if (step === StepKey.SelectBlastType) {
    return [
      {
        text: 'Cancel',
        cancel: true,
        dataTestAttrs: {component: 'BLASTModal', element: 'cancel'},
      },
      {
        text: 'Next',
        submit: true,
        onClick: () => {
          setCurrentStepKey(StepKey.SelectSequences);
          // add the default params in now that the blast type is chosen
          setFilter((previousFilter) => ({
            ...previousFilter,
            params: getBLASTParamsFromFilter(filter),
          }));
        },
        dataTestAttrs: {component: 'BLASTModal', element: 'next'},
      },
    ];
  } else if (step === StepKey.SelectSequences) {
    const textInputValidationState = validateBLASTSequence(
      sequenceWithoutWhitespace,
      filter.operator as BLASTOperator
    );

    const sequenceInputIsNotValid = !sequenceWithoutWhitespace || !textInputValidationState.isValid;
    const noValidTextSequences =
      !filter.value.textSequences.length ||
      filter.value.textSequences.every(
        (ts) => !validateBLASTSequence(stripWhitespace(ts.sequence), filter.operator as BLASTOperator).isValid
      );
    const noValidBenchlingEntities = !filter.value.apiIdentifiers.length;
    return [
      {
        text: 'Back',
        onClick: () => {
          setCurrentStepKey(StepKey.SelectBlastType);
        },
        dataTestAttrs: {component: 'BLASTModal', element: 'back'},
      },
      {
        text: 'Next',
        submit: true,
        onClick: async () => {
          // If there is text in the residues input that the user hasn't added yet, add it as well since it's
          // likely they just forgot to do it.
          const newSequenceLabel = makeSequenceLabel(sequenceWithoutWhitespace, filter.value.textSequences);
          const resultFilter = sequenceWithoutWhitespace
            ? {
                ...filter,
                value: {
                  ...filter.value,
                  textSequences: [
                    ...filter.value.textSequences,
                    {
                      name: newSequenceLabel,
                      sequence: sequenceWithoutWhitespace,
                    },
                  ],
                },
              }
            : filter;
          setFilter(resultFilter);

          // set default params, w/ short recommendations if applicable
          const areShort = await shouldUseShortProgram(
            resultFilter.value.apiIdentifiers,
            resultFilter.value.textSequences,
            resultFilter.operator as BLASTOperator
          );
          let task: BLASTNTask | BLASTPTask | BLASTXTask | TBLASTNTask | undefined;
          if (areShort && resultFilter.operator === Operator.BLASTN) {
            task = BLASTNTask.BLASTNShort;
          } else if (areShort && resultFilter.operator === Operator.BLASTP) {
            task = BLASTPTask.BLASTPShort;
          }
          setFilter((prevFilter) => ({
            ...prevFilter,
            params: getDefaultBLASTParamsForTask(prevFilter, task),
          }));

          const resultInputSources = sequenceWithoutWhitespace
            ? {
                ...inputSources,
                [newSequenceLabel]: BLASTInputSource.TextInput,
              }
            : inputSources;
          setInputSources(resultInputSources);
          setCurrentStepKey(StepKey.SetParameters);
        },
        dataTestAttrs: {component: 'BLASTModal', element: 'next'},
        disabled:
          fileExtractionsInProgress > 0 ||
          (noValidTextSequences && noValidBenchlingEntities && sequenceInputIsNotValid),
      },
    ];
  } else {
    // leaving for third step: additional parameters
    const textInputValidationState = validateBLASTSequence(
      sequenceWithoutWhitespace,
      filter.operator as BLASTOperator
    );

    const sequenceInputIsNotValid = !sequenceWithoutWhitespace || !textInputValidationState.isValid;
    const noValidTextSequences =
      !filter.value.textSequences.length ||
      filter.value.textSequences.every(
        (ts) => !validateBLASTSequence(stripWhitespace(ts.sequence), filter.operator as BLASTOperator).isValid
      );
    const noValidBenchlingEntities = !filter.value.apiIdentifiers.length;
    const numericParamsInvalid =
      filter.params?.culling_limit == null || filter.params?.evalue == null || filter.params?.evalue === 0;
    return [
      {
        text: 'Back',
        onClick: () => {
          setCurrentStepKey(StepKey.SelectSequences);
        },
        dataTestAttrs: {component: 'BLASTModal', element: 'back'},
      },
      {
        text: 'Run BLAST',
        submit: true,
        onClick: () => {
          onRequestSubmit(filter);
          trackBlastSubmission(filter.operator as BLASTOperator, new Set(Object.values(inputSources)));
        },
        dataTestAttrs: {component: 'BLASTModal', element: 'submit'},
        disabled:
          fileExtractionsInProgress > 0 ||
          (noValidTextSequences && noValidBenchlingEntities && sequenceInputIsNotValid) ||
          numericParamsInvalid,
      },
    ];
  }
}

export interface BLASTModalProps {
  initialFilter: BLASTFilter;
  onRequestSubmit: (newFilter: BLASTFilter) => void;
  onRequestClose: () => void;
  currentStep?: StepKey;
}

export default function BLASTModal({
  initialFilter,
  onRequestSubmit,
  onRequestClose,
  currentStep,
}: BLASTModalProps): JSX.Element {
  const [currentStepKey, setCurrentStepKey] = useState(currentStep ?? StepKey.SelectBlastType);
  const [filter, setFilter] = useState(initialFilter);
  const [sequence, setSequence] = useState('');
  const [fileExtractionsInProgress, setFileExtractionsInProgress] = useState(0);
  /*
   * This state tracks each sequence and its source.
   * For text sequences, the key is the sequence label.
   * For benchling entities, the key is the entity id.
   */
  const [inputSources, setInputSources] = useState<Record<string, BLASTInputSource>>({});

  const sequenceWithoutWhitespace = stripWhitespace(sequence);

  const buttons = getStepModalButtons(
    currentStepKey,
    setCurrentStepKey,
    sequenceWithoutWhitespace,
    filter,
    setFilter,
    fileExtractionsInProgress,
    onRequestSubmit,
    inputSources,
    setInputSources
  );

  return (
    <ExpandableModal
      enableRedesign={true}
      title={BLAST_MODAL_TITLE}
      buttons={buttons}
      onRequestClose={() => {
        trackBlastModalClose(BlastModalTrigger.BLAST_SEARCH, currentStepKey);
        onRequestClose();
      }}
      mediumLarge={true}
      expandHeight={true}
      scrollable={true}
    >
      <div className={css(styles.stepNavMarginAdjustment)}>
        <StepNav<StepKey>
          steps={[
            {
              key: StepKey.SelectBlastType,
              title: 'Select BLAST type',
              metadata: getSelectBlastTypeStepTitle(currentStepKey, filter.operator),
            },
            {
              key: StepKey.SelectSequences,
              title: 'Select sequences',
              metadata: getSelectSequencesStepTitle(currentStepKey, filter),
            },
            {
              key: StepKey.SetParameters,
              title: 'Set parameters',
            },
          ]}
          currentStepKey={currentStepKey}
          onUpdateCurrentStep={(newStepKey: StepKey): void => {
            setCurrentStepKey(newStepKey);
          }}
          statusByStep={{
            [StepKey.SelectBlastType]: null,
            [StepKey.SelectSequences]:
              currentStepKey === StepKey.SetParameters ? null : StepItemStatus.UNVISITED,
            [StepKey.SetParameters]: StepItemStatus.UNVISITED,
          }}
          size={StepNavSize.LARGE}
        />
      </div>
      <>
        {currentStepKey === StepKey.SelectBlastType && (
          <SelectBlastTypePane filter={filter} setFilter={setFilter} setInputSources={setInputSources} />
        )}
        {currentStepKey === StepKey.SelectSequences && (
          <SelectSequencesPane
            filter={filter}
            setFilter={setFilter}
            setInputSources={setInputSources}
            sequence={sequence}
            setSequence={setSequence}
            fileExtractionsInProgress={fileExtractionsInProgress}
            setFileExtractionsInProgress={setFileExtractionsInProgress}
          />
        )}
        {currentStepKey === StepKey.SetParameters && (
          <SetParametersPane filter={filter} setFilter={setFilter} setInputSources={setInputSources} />
        )}
      </>
    </ExpandableModal>
  );
}
